import React, { useState, useMemo } from "react";
import throttle from "lodash/throttle";
import {
    Grid,
    TextField,
    Typography,
    Button,
    useTheme,
    Paper,
    MenuItem
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { searchUsers } from "../modules/Users";
import { useAtom } from "jotai";
import { companyIdAtom, openAddClientModalAtom } from "../atoms/atoms";
import { clientByUserId } from "../graphql/queries";
import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import AddIcon from "@mui/icons-material/Add";
import AddClientModal from "./AddClientModal";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Box } from "@mui/system";

export const ClientSelector = ({
    setClient,
    disableElements,
    defaultValue,
    margin = "8px 0px",
    size,
    autoFocus = false,
    hideInputEmailDisplay = false,
    placeholder = "Type to search",
    label,
    required,
    value,
    hideAddClient = false,
    inputRef = null,
    focus = false
}) => {
    const _filterOptions = createFilterOptions();
    const [clients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [openAddClientModal, setOpenAddClientModal] = useAtom(
        openAddClientModalAtom
    );
    const [companyId] = useAtom(companyIdAtom);
    const addClientOption = { id: "addClient", label: "Add client" };
    const defaultOption = value
        ? value
        : !hideAddClient
          ? addClientOption
          : null;
    const [selectedClient, setSelectedClient] = useState(defaultOption);
    const fetch = useMemo(
        () => throttle(async (value) => searchUsers(value, companyId), 200),
        [companyId]
    );
    const clientsDisplay = clients.map(
        ({ emailaddress = "", firstname = "", lastname = "", id }) => ({
            title: `${firstname} ${lastname}`,
            id,
            label: `${firstname} ${lastname} ${
                hideInputEmailDisplay ? "" : `(${emailaddress})`
            }`,
            email: `${emailaddress}`
        })
    );
    const optionsWithAddClient = [...clientsDisplay, addClientOption];
    const filterOptions = (options, state) => {
        const results = _filterOptions(options, state);

        const isStickyOptionPresent = results.some(
            (option) => option.id === addClientOption.id
        );

        if (!isStickyOptionPresent) {
            results.push(addClientOption);
        }

        return results;
    };

    const handleAddClient = () => {
        setOpenAddClientModal(true);
    };
    const handleChangeClient = async (e, value) => {
        if (value) {
            const { id: userId } = value;
            try {
                const { data, errors } = await graphql(
                    graphqlOperation(clientByUserId, { userId: userId })
                );
                if (errors) {
                    throw errors;
                }
                setClient(data.clientByUserId.items[0]);
            } catch (e) {}
        } else {
            setClient();
            return;
        }
    };

    const handleOptionChange = (event, value) => {
        if (value && value.id === "addClient") {
            handleAddClient();
        } else {
            handleChangeClient(event, value);
        }
        setSelectedClient(value);
    };
    const handler = async ({ target: { value } }) => {
        const users =
            typeof value === "string" && value !== "" ? await fetch(value) : [];
        setSearchTerm(value);
        setClients(users);
    };

    const handleBlur = () => {
        if (!selectedClient) {
            setClients([]);
        }
    };

    const CustomClientDropdown = (props) => {
        const { children, ...paperProps } = props;
        const theme = useTheme();

        return (
            <Paper {...paperProps}>
                <Box
                    sx={{
                        maxHeight: 300,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column"
                    }}
                >
                    {children}
                </Box>
                {!hideAddClient && (
                    <Box
                        sx={{
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: theme.palette.background.paper,
                            padding: "0px",
                            borderTop:
                                optionsWithAddClient.length > 1 &&
                                `1px solid ${theme.palette.divider}`,
                            zIndex: 1
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleAddClient();
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        <MenuItem
                            {...props}
                            onClick={(event) => {
                                event.stopPropagation(); //prevents the Autocomplete from selecting this option
                                handleAddClient();
                            }}
                            sx={{
                                listStyleType: "none",
                                boxShadow: "none",
                                padding:
                                    optionsWithAddClient.length > 1
                                        ? "6px 16px"
                                        : "4px 16px",
                                height:
                                    optionsWithAddClient.length > 1
                                        ? "40px"
                                        : "36px"
                            }}
                        >
                            <Button
                                variant="text"
                                fullWidth
                                disableRipple
                                startIcon={
                                    <AddIcon
                                        sx={{
                                            color: theme.palette.primary.main
                                        }}
                                    />
                                }
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    color: theme.palette.primary.main,
                                    height: "28px !important",
                                    textTransform: "none"
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleAddClient();
                                }}
                            >
                                Add client
                            </Button>
                        </MenuItem>
                    </Box>
                )}
            </Paper>
        );
    };

    return (
        <>
            <Autocomplete
                style={{
                    width: "100%",
                    margin: margin
                }}
                noOptionsText={"No Clients Found"}
                options={optionsWithAddClient}
                onBlur={() => {
                    handleBlur();
                }}
                getOptionLabel={(option) =>
                    option
                        ? option.id === "addClient"
                            ? ""
                            : option.label
                        : ""
                }
                componentsProps={{
                    popper: {
                        sx: {
                            "& .MuiAutocomplete-listbox": {
                                padding: "0px"
                            }
                        }
                    },
                    paper: {
                        component: CustomClientDropdown
                    }
                }}
                renderOption={(props, option) => {
                    if (option.id !== "addClient") {
                        return (
                            <li {...props}>
                                <Grid
                                    container
                                    direction="column"
                                    sx={{
                                        width: "100%",
                                        gap: hideAddClient && "4px"
                                    }}
                                >
                                    <Typography
                                        noWrap
                                        sx={{
                                            lineHeight: "14px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            width: "100%"
                                        }}
                                    >
                                        {option.title}
                                    </Typography>
                                    <Typography
                                        noWrap
                                        variant="helperText"
                                        sx={{
                                            lineHeight: "14px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            width: "100%"
                                        }}
                                    >
                                        {option.email}
                                    </Typography>
                                </Grid>
                            </li>
                        );
                    }
                    return null;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        fullWidth
                        required={required ? required : false}
                        label={label ? label : "Client"}
                        onChange={handler}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        inputRef={inputRef}
                    />
                )}
                openOnFocus={hideAddClient ? false : true}
                filterOptions={filterOptions}
                size={size}
                onChange={handleOptionChange}
                disabled={disableElements}
                value={selectedClient}
                focus={focus}
            />
            {!hideAddClient && (
                <AddClientModal
                    handleOnCreate={(client) => {
                        setOpenAddClientModal(false);
                        setClients([
                            {
                                firstname: client.user.firstname,
                                lastname: client.user.lastname,
                                emailaddress: client.user.emailaddress,
                                id: client.id
                            }
                        ]);
                        setClient(client);
                        const newClient = {
                            id: client.id,
                            title: `${client.user.firstname} ${client.user.lastname}`,
                            email: client.user.emailaddress,
                            label: `${client.user.firstname} ${client.user.lastname}`
                        };
                        setSelectedClient(newClient);
                    }}
                />
            )}
        </>
    );
};
